import React, { PropsWithChildren, useEffect } from 'react';
import { CsvTemplateImporter } from './CsvTemplateImporter';
import { useImportersIntl } from './translations/useImportersIntl';
import { useTenantConfig } from 'src/utils/hooks/useTenantConfig';
import { useManagingSources } from 'src/utils/hooks/data/useManagingSources';
import { EngagementManagingSourceModel } from '@utmost.co/enterprise-models';
import { useFeatureFlags } from 'src/components/common/featureFlag/useFeatureFlags';
import { AllowedImportType } from './utils/useImporterData';
import { useImporterTypeOptions } from './utils/useImporterTypeOptions';
import { useMixpanel } from 'src/integrations/mixpanel/useMixpanel';
import { MixpanelEvents } from 'src/integrations/mixpanel/Events/MixpanelEvents';
import { Select, Input, Accordion, ChipsSelect, Typography, styled } from '@beeline.com/bp-design-system';

export interface ImportFormEvent {
  comment: string;
  importType: AllowedImportType;
  managingSources: EngagementManagingSourceModel;
}

interface Props {
  allowedImportTypes: AllowedImportType[] | undefined;
  importType?: AllowedImportType;
  managingSource?: EngagementManagingSourceModel;
  onCommentChanged: (comment: string) => void;
  onImportTypeChanged: (importType: AllowedImportType) => void;
  onManagingSourceChanged: (managingSource: EngagementManagingSourceModel) => void;
  loadingImportData?: boolean;
}

export const ImportForm: React.FC<PropsWithChildren<Props>> = ({
  allowedImportTypes,
  importType,
  managingSource = 'UTMOST',
  onCommentChanged,
  onImportTypeChanged,
  onManagingSourceChanged,
  loadingImportData
}) => {
  const intl = useImportersIntl();
  const tenantConfig = useTenantConfig();
  const getImporterTypeOptions = useImporterTypeOptions();
  const hasOnlyOneImportType = allowedImportTypes?.length === 1;
  const { track } = useMixpanel();

  const { isEnabledSubFeature } = useFeatureFlags();
  const contractSubFeatureEnabled = isEnabledSubFeature('sourcing', 'contract');

  const { managingSourcesOptions, loading } = useManagingSources();
  const showImportSources = tenantConfig?.features?.['show-import-sources']?.enabled ?? false;

  const importTypeOptions = !contractSubFeatureEnabled
    ? allowedImportTypes?.filter((option) => option.entityType !== 'CONTRACT' && option.entityType !== 'MILESTONE')
    : allowedImportTypes;

  // biome-ignore lint/correctness/useExhaustiveDependencies: Only want to fire this once
  useEffect(() => {
    if (hasOnlyOneImportType && importType) {
      track(MixpanelEvents.importerEvents.DATA_TYPE_PRESELECT, { importType: importType.entityType });
    }
  }, []);

  return (
    <>
      <Select
        autoComplete
        dataTest="importer-data-type"
        // Disable only if only 1 item allowed
        disabled={hasOnlyOneImportType}
        fullWidth
        label={intl.dataType}
        listBoxProps={{ scrollSelectedItemIntoView: true }}
        loading={loadingImportData}
        menuProps={{
          hideSelectedOptions: false,
          sort: true
        }}
        onChange={(importType: AllowedImportType) => {
          track(MixpanelEvents.importerEvents.DATA_TYPE_SELECT, { importType: importType?.entityType });
          onImportTypeChanged(importType);
        }}
        options={getImporterTypeOptions(importTypeOptions)}
        placeholder={intl.selectDataType}
        required
        disableClearable
        value={importType}
      />
      <StyledSpacer />
      <Input
        dataTest="add_comment_text_box"
        fullWidth
        label={intl.note}
        onChange={(e) => onCommentChanged(e.target.value)}
        placeholder={intl.addANoteAboutThisImport}
        required
      />
      <StyledSpacer />
      {showImportSources && (
        <ChipsSelect<EngagementManagingSourceModel>
          dataTest="managingSources"
          label={intl.dataSource}
          loading={loading}
          onSelect={(e) => (e ? onManagingSourceChanged(e) : undefined)}
          options={managingSourcesOptions}
          value={managingSource}
        />
      )}
      <StyledSpacer />
      <div>
        <StyledTypography variant="label1">{intl.importTemplates}</StyledTypography>
        <Accordion variant="outlined">
          <Accordion.Summary>
            <StyledTitle>{intl.templates}</StyledTitle>
          </Accordion.Summary>
          <Accordion.Details>
            <CsvTemplateImporter importType={importType} key={importType?.id} />
          </Accordion.Details>
        </Accordion>
      </div>
    </>
  );
};

const StyledSpacer = styled('div')`
  && {
    margin-bottom: ${({ theme }) => theme.spacingSize.l};
  }
`;

const StyledTypography = styled(Typography)`
  && {
    margin-bottom: ${({ theme }) => theme.spacingSize.m};
  }
`;

const StyledTitle = styled('div')`
  margin-left: ${({ theme }) => theme.spacingSize.m};
`;
